import { ChangeDetectorRef, Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { AgGridSandbox } from '@iris/common/modules/grid/ag-grid/ag-grid.sandbox';
import {
  AgGridAddPagingPanelDirective,
} from '@iris/common/modules/grid/ag-grid/extensions/pagination/add-paging-panel.directive';
import { IrisGridMode } from '@iris/common/modules/grid/ag-grid/models/iris-grid-mode';
import { AgGridAngular } from 'ag-grid-angular';
import { FilterChangedEvent, FirstDataRenderedEvent, RowDataUpdatedEvent } from 'ag-grid-community';
import { merge, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Directive({
  selector: 'ag-grid-angular[irisGridMode]',
  providers: [AgGridSandbox],
  standalone: false,
})
export class AgGridClientPaginationDirective implements OnInit, OnDestroy, OnChanges {
  private readonly agGrid = inject(AgGridAngular);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly gridSandbox = inject(AgGridSandbox);
  private readonly addPagingPanel = inject(AgGridAddPagingPanelDirective, { optional: true });

  @Input('irisGridMode') gridMode: IrisGridMode;

  isGridRendered = false;
  destroyedSubject = new Subject<void>();

  ngOnInit(): void {
    this.initPagination();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.gridMode) {
      this.initPagination();
    }
  }

  ngOnDestroy(): void {
    this.destroyedSubject.next();
    this.destroyedSubject.complete();
  }

  private initPagination(): void {
    this.destroyedSubject.next();

    if (this.gridMode === IrisGridMode.Server || !this.addPagingPanel) return;

    merge(
      this.agGrid.firstDataRendered,
      this.agGrid.filterChanged,
      this.agGrid.rowDataUpdated,
    ).pipe(
      tap((event: FirstDataRenderedEvent | FilterChangedEvent | RowDataUpdatedEvent) => {
        if (!this.isGridRendered) {
          this.addPagingPanel.totalItemsCount = this.agGrid.api.getDisplayedRowCount();
          this.isGridRendered = true;
        } else {
          this.addPagingPanel.totalItemsCount = event.api.getDisplayedRowCount();
          if (!this.addPagingPanel.page) {
            this.addPagingPanel.page = 1;
          }
        }

        this.changeDetector.markForCheck();
        this.gridSandbox.updatePropsForClientGrid();
      }),
      takeUntil(this.destroyedSubject),
    ).subscribe();
  }
}

@Directive({
  selector: `ag-grid-angular[irisGridModeStandalone]`,
  providers: [AgGridSandbox],
  standalone: true,
})
export class AgGridClientPaginationStandaloneDirective extends AgGridClientPaginationDirective {}
